import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Autocomplete,
  Chip,
  Checkbox,
  ListItemText,
  FormHelperText,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { CreateEntityRequest, getAvailableDataProviders } from '../../services/entityService';
import { UserService, UserInfo } from '../../services/userService';

interface CreateEntityDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: CreateEntityRequest) => void;
}

export const CreateEntityDialog = ({ open, onClose, onCreate }: CreateEntityDialogProps) => {
  const { control, register, handleSubmit, formState, reset } = useForm<CreateEntityRequest>({
    defaultValues: {
      UserID: '',
      Role: 'fleetManager',
      AccountName: '',
      MohaAccountNumber: '',
      MohaEuaBalance: 0,
      DataProviders: ['SignalOcean'], // Set Signal Ocean as default
    },
  });

  // Fetch users for the dropdown
  const {
    data: usersData,
    isError,
    error,
  } = useQuery({
    queryKey: ['users', 'create-entity'],
    queryFn: UserService.getAllUsers,
    enabled: open,
  });

  const { data: providersData } = useQuery({
    queryKey: ['available-data-providers'],
    queryFn: getAvailableDataProviders,
    enabled: open,
  });

  // Log errors for debugging but don't show toast (dialog will still work without users)
  useEffect(() => {
    if (isError && error && open) {
      console.error('Failed to load users for entity creation:', error);
    }
  }, [isError, error, open]);

  const users: UserInfo[] = usersData?.users || [];
  const availableProviders = providersData?.providers || [];

  const onSubmit = handleSubmit((data) => {
    onCreate(data);
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Entity</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Controller
            name="UserID"
            control={control}
            rules={{ required: 'User is required' }}
            render={({ field, fieldState }) => (
              <Autocomplete
                {...field}
                options={users}
                getOptionLabel={(user) => UserService.formatDisplayName(user)}
                value={users.find((user) => user.uid === field.value) || null}
                onChange={(_, newValue) => field.onChange(newValue?.uid || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign to User *"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
                renderOption={(props, user) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{UserService.formatDisplayName(user)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            )}
          />

          <FormControl fullWidth>
            <InputLabel>Role *</InputLabel>
            <Select
              {...register('Role', { required: 'Role is required' })}
              label="Role *"
              defaultValue="fleetManager"
            >
              <MenuItem value="fleetManager">Fleet Manager</MenuItem>
              <MenuItem value="trader">Trader</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Account Name *"
            {...register('AccountName', { required: 'Account Name is required' })}
            fullWidth
            error={!!formState.errors.AccountName}
            helperText={formState.errors.AccountName?.message}
          />

          <TextField
            label="MOHA Account Number *"
            {...register('MohaAccountNumber', { required: 'MOHA Account Number is required' })}
            fullWidth
            error={!!formState.errors.MohaAccountNumber}
            helperText={formState.errors.MohaAccountNumber?.message}
          />

          <TextField
            label="Initial EUA Balance"
            type="number"
            {...register('MohaEuaBalance', {
              valueAsNumber: true,
              min: { value: 0, message: 'Balance cannot be negative' },
            })}
            fullWidth
            error={!!formState.errors.MohaEuaBalance}
            helperText={formState.errors.MohaEuaBalance?.message || 'Default: 0'}
          />

          <TextField
            label="Trader Name"
            {...register('TraderName')}
            fullWidth
            error={!!formState.errors.TraderName}
            helperText={
              formState.errors.TraderName?.message || 'Optional: Trader name for EUA procurement'
            }
          />

          <TextField
            label="Trader Email"
            type="email"
            {...register('TraderEmail', {
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
            fullWidth
            error={!!formState.errors.TraderEmail}
            helperText={
              formState.errors.TraderEmail?.message ||
              'Optional: Email for EUA procurement requests'
            }
          />

          <TextField
            label="Report Email"
            type="email"
            {...register('ReportEmail', {
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
            fullWidth
            error={!!formState.errors.ReportEmail}
            helperText={
              formState.errors.ReportEmail?.message ||
              'Optional: Email for monthly emissions report notifications'
            }
          />

          {/* Data Providers Selection */}
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select Data Providers</InputLabel>
              <Controller
                name="DataProviders"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    multiple
                    value={field.value || []}
                    label="Select Data Providers"
                    onChange={(e) => field.onChange(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {availableProviders.map((provider) => (
                      <MenuItem key={provider} value={provider}>
                        <Checkbox checked={(field.value || []).includes(provider)} />
                        <ListItemText primary={provider} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <FormHelperText>
              Select which data providers this entity can use for voyage data
            </FormHelperText>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained" disabled={!formState.isValid}>
          Create Entity
        </Button>
      </DialogActions>
    </Dialog>
  );
};
